import {
  type PricingPlan,
  PricingTabType,
  type Product,
  type Validity
} from 'Services/ProductManagement/Features/Product/types'
import type { ZoneDefinition } from 'Services/ProductManagement/Features/ZoneDefinition/types'
import { calculateISODurationLengthInMinutes } from 'Util/formatters'
import getTranslation from 'Util/getTranslation'
import {
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Typography
} from '@mui/material'
import { t } from 'ttag'

interface SpecialZonePricingProps {
  zoneDefinition: ZoneDefinition
  product: Product
  pricingPlans: PricingPlan[]
  activePeriod: Validity | null
  pricingGridType: PricingTabType
  domicileClass?: string
  currentSpecialZonePricingPlans: PricingPlan[]
  handleSpecialZonePricingPlanPriceChange: (
    zoneMap: string[],
    price: string,
    specialZonePricingPlans: PricingPlan[]
  ) => void
}

function SpecialZonePricing({
  zoneDefinition,
  pricingPlans,
  activePeriod,
  pricingGridType,
  domicileClass,
  currentSpecialZonePricingPlans,
  handleSpecialZonePricingPlanPriceChange
}: Readonly<SpecialZonePricingProps>) {
  const isExchangePeriodTab = pricingGridType === PricingTabType.EXCHANGE_PERIOD

  const getPricingPlanTranslation = (zoneMap: string[]) => {
    return zoneMap
      .map(zoneId => getTranslation(zoneDefinition.translations[zoneId], 'fi').value)
      .join('+')
  }

  /**
   * Gets the price of the special zone pricing plan for the given zone map.
   *
   * @param zoneMap
   * @param specialZonePricingPlans
   * @returns {price | ''} The price of the special zone pricing plan for the given zone map, or an empty string if not found.
   */
  const getSpecialZonePricingPlanPrice = (
    zoneMap: string[],
    specialZonePricingPlans: PricingPlan[]
  ) => {
    const specialZonePricingPlan = specialZonePricingPlans.find(
      pricingPlan =>
        pricingPlan.active_period?.title === activePeriod?.title &&
        pricingPlan.domicile_class === (domicileClass ?? null) &&
        pricingPlan.zone_map.join('') === zoneMap.join('')
    )

    if (isExchangePeriodTab) {
      return specialZonePricingPlan?.usage_validity_duration
        ? calculateISODurationLengthInMinutes(specialZonePricingPlan.usage_validity_duration)
        : ''
    } else {
      return specialZonePricingPlan?.pricings[0].price ?? ''
    }
  }

  const isSpecialZonePricingPlanDisabled = (zoneMap: string[]) => {
    if (isExchangePeriodTab) {
      const specialZonePricingPlan = pricingPlans.find(
        pricingPlan =>
          pricingPlan.domicile_class === (domicileClass ?? null) &&
          pricingPlan.zone_map.join('') === zoneMap.join('')
      )
      return specialZonePricingPlan === undefined || specialZonePricingPlan?.pricings.length === 0
    }
    return false
  }
  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        variant="subtitle2"
        fontWeight="bold"
        sx={{ my: 2 }}
      >{t`Additional zones`}</Typography>
      <Box display="flex" flexDirection="column" gap={2}>
        {zoneDefinition.special_zones.map((zoneMap, _index) => {
          return (
            <FormControl
              sx={{ width: 129 }}
              variant="outlined"
              key={`${getPricingPlanTranslation(zoneMap)}-zone-price`}
            >
              <InputLabel htmlFor={`${getPricingPlanTranslation(zoneMap)}-zone-price`}>
                {getPricingPlanTranslation(zoneMap)}
              </InputLabel>
              <OutlinedInput
                id={`${getPricingPlanTranslation(zoneMap)}-zone-price`}
                type="number"
                endAdornment={
                  <InputAdornment position="end">
                    {isExchangePeriodTab ? 'min' : '€'}
                  </InputAdornment>
                }
                label={getPricingPlanTranslation(zoneMap)}
                sx={{ backgroundColor: 'background.default' }}
                aria-describedby="standard-weight-helper-text"
                onChange={e =>
                  handleSpecialZonePricingPlanPriceChange(
                    zoneMap,
                    e.target.value,
                    currentSpecialZonePricingPlans
                  )
                }
                disabled={isSpecialZonePricingPlanDisabled(zoneMap)}
                value={getSpecialZonePricingPlanPrice(zoneMap, pricingPlans)}
              />
            </FormControl>
          )
        })}
      </Box>
    </Box>
  )
}

export default SpecialZonePricing
